import { useState } from "react";
import { Card, CardBody, Chip, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem, Button } from "@heroui/react";
import { ArrowUpRight, ArrowDownRight, Export, FileXls, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";
import { pctChange } from "@/components/PeriodFilter";
import { exportExcel, exportPdf } from "@/lib/reportExport";

export const BLUE = "#1E7FD4", GOLD = "#F5B921", GREEN = "#16a34a", RED = "#dc2626", AMBER = "#f59e0b";
export const kFmt = (v) => (Math.abs(v) >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`);
export const axis = { tick: { fontSize: 11 }, tickLine: false };
export const days = (v) => (v == null ? "—" : `${Number(v).toFixed(1)} d`);

export const Delta = ({ cur, prev, invert, suffix = "", testid }) => {
  if (prev == null || cur == null) return null;
  if (!prev) return cur ? <Chip size="sm" variant="flat" className="mt-2 font-semibold text-muted-foreground h-auto whitespace-normal" title="No data in the previous period" data-testid={testid}>New vs previous period</Chip> : null;
  const pct = pctChange(cur, prev);
  const up = pct > 0.05, down = pct < -0.05;
  const improved = invert ? cur < prev : cur > prev;
  const color = !up && !down ? "default" : improved ? "success" : "danger";
  return <Chip size="sm" variant="flat" color={color} className="mt-2 font-semibold h-auto whitespace-normal" classNames={{ content: "whitespace-normal leading-tight py-0.5" }} startContent={up ? <ArrowUpRight size={12} weight="bold" /> : down ? <ArrowDownRight size={12} weight="bold" /> : null} data-testid={testid}>{`${pct > 0 ? "+" : ""}${pct.toFixed(1)}%${suffix}`}</Chip>;
};

const TONE = { gold: "bg-[#F5B921]/15 text-[#B8860B]", green: "bg-emerald-500/10 text-emerald-600", red: "bg-red-500/10 text-red-600", primary: "bg-[#1E7FD4]/10 text-[#1E7FD4]" };

export const Kpi = ({ label, value, icon: Icon, tone = "primary", testid, hint, delta }) => (
  <Card shadow="none" className="hp-card" data-testid={testid}><CardBody className="p-4 sm:p-5">
    <div className="flex items-start justify-between gap-3"><p className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</p><span className={`rounded-xl p-2 ${TONE[tone] || TONE.primary}`}><Icon size={18} weight="duotone" /></span></div>
    <p className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-2 tabular-nums">{value}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    {delta}
  </CardBody></Card>
);

export const Panel = ({ icon: Icon, title, subtitle, children, testid, action }) => (
  <Card shadow="none" className="hp-card" data-testid={testid}><CardBody className="p-5 sm:p-6">
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5"><div><h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight flex items-center gap-2"><Icon size={20} className="text-[#1E7FD4]" weight="duotone" /> {title}</h2>{subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}</div>{action}</div>
    {children}
  </CardBody></Card>
);

export const Table = ({ cols, rows, render, onRow, testidFor }) => (
  <div className="border border-border/60 rounded-xl overflow-x-auto"><table className="w-full text-sm">
    <thead><tr className="text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">{cols.map(([c, right]) => <th key={c} className={`px-4 py-2.5 font-semibold whitespace-nowrap ${right ? "text-right" : ""}`}>{c}</th>)}</tr></thead>
    <tbody>{rows.map((r, i) => <tr key={i} onClick={onRow ? () => onRow(r) : undefined} className={`border-t border-border/60 ${onRow ? "cursor-pointer hover:bg-[#1E7FD4]/5" : ""}`} data-testid={testidFor?.(r)}>{render(r)}</tr>)}</tbody>
  </table></div>
);

export const Empty = ({ children }) => <p className="text-sm text-muted-foreground" data-testid="report-empty">{children}</p>;

export function ExportButton({ tabId, title, periodLabel = "", sheets }) {
  const [busy, setBusy] = useState(false);
  const file = `${title.replace(/\s+/g, "_")}_${String(periodLabel).replace(/[^\w]+/g, "-")}`;
  const run = async (k) => {
    setBusy(true);
    try {
      if (k === "xlsx") exportExcel(typeof sheets === "function" ? sheets() : sheets, file, periodLabel);
      else await exportPdf(tabId, title, periodLabel, file);
      toast.success(`${title} exported as ${k === "xlsx" ? "Excel" : "PDF"}`);
    } catch (e) { toast.error("Export failed"); } finally { setBusy(false); }
  };
  return (
    <Dropdown placement="bottom-end"><DropdownTrigger><Button size="sm" variant="flat" color="primary" radius="lg" isLoading={busy} startContent={<Export size={14} weight="bold" />} data-testid={`export-${tabId}`}>Export</Button></DropdownTrigger>
      <DropdownMenu aria-label="Export" onAction={run}>
        <DropdownItem key="xlsx" startContent={<FileXls size={16} className="text-emerald-600" />} data-testid={`export-${tabId}-xlsx`}>Excel (.xlsx)</DropdownItem>
        <DropdownItem key="pdf" startContent={<FilePdf size={16} className="text-red-600" />} data-testid={`export-${tabId}-pdf`}>PDF (as shown)</DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

export const TabShell = ({ id, title, periodLabel, sheets, children }) => (
  <div className="space-y-6" id={id} data-testid={`${id}-section`}>
    <div className="flex justify-end -mb-2"><ExportButton tabId={id} title={title} periodLabel={periodLabel} sheets={sheets} /></div>
    {children}
  </div>
);
